import React, { useEffect, useMemo, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  ComposedChart,
} from "recharts";
import { fetchAllSnapshots } from "@/lib/api";
import RegionSelect from "@/components/RegionSelect";
import DualExplainer from "@/components/DualExplainer";

export default function Weather() {
  const [data, setData] = useState(null);
  const [regionId, setRegionId] = useState("");

  useEffect(() => {
    (async () => {
      const d = await fetchAllSnapshots();
      setData(d);
      if (!regionId && d?.regions?.[0]) setRegionId(d.regions[0].id);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const snap = data?.snapshots?.find((s) => s.region_id === regionId);
  const regions = data?.regions || [];

  const rows = useMemo(() => {
    if (!snap) return [];
    const d = snap.weather?.dates || [];
    return d.map((date, i) => ({
      date: date?.slice(5),
      et0: snap.weather.et0?.[i],
      sm07: snap.weather.soil_moisture_0_7cm?.[i],
      sm728: snap.weather.soil_moisture_7_28cm?.[i],
      temp: snap.weather.temperature_2m_mean?.[i],
      gdd: snap.weather.gdd_cumulative?.[i],
    }));
  }, [snap]);

  return (
    <div className="space-y-6" data-testid="weather-root">
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="eid-label">Open-Meteo · NWP</div>
          <h1 className="text-3xl md:text-4xl font-medium tracking-tight mt-1">Wetter & Boden</h1>
          <p className="text-sm text-zinc-400 mt-1 max-w-2xl">
            ET₀, volumetrische Bodenfeuchte und kumulierte Wärmesumme (GDD, Basis 10 °C).
          </p>
        </div>
        <div data-testid="region-selector">
          <RegionSelect regions={regions} value={regionId} onChange={setRegionId} testid="weather-region-select" />
        </div>
      </header>

      <section className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div className="eid-card p-4" data-testid="chart-et0">
          <h3 className="text-lg font-medium">Evapotranspiration (ET₀)</h3>
          <div className="eid-label mt-1">mm/Tag · letzte 30 Tage</div>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={rows} margin={{ top: 16, right: 12, left: -16, bottom: 0 }}>
              <CartesianGrid stroke="rgba(255,255,255,0.08)" strokeDasharray="2 4" />
              <XAxis dataKey="date" fontSize={10} tickLine={false} stroke="#8A8A93" />
              <YAxis fontSize={10} tickLine={false} stroke="#8A8A93" />
              <Tooltip contentStyle={{ background: "#1A1A1A", border: "none", fontFamily: "JetBrains Mono", fontSize: 11, color: "white" }} />
              <Line type="monotone" dataKey="et0" stroke="#FFC24B" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="eid-card p-4" data-testid="chart-soil">
          <h3 className="text-lg font-medium">Bodenfeuchte · Schichten</h3>
          <div className="eid-label mt-1">m³/m³ · 0–10 cm &amp; 10–40 cm</div>
          <ResponsiveContainer width="100%" height={240}>
            <ComposedChart data={rows} margin={{ top: 16, right: 12, left: -16, bottom: 0 }}>
              <CartesianGrid stroke="rgba(255,255,255,0.08)" strokeDasharray="2 4" />
              <XAxis dataKey="date" fontSize={10} tickLine={false} stroke="#8A8A93" />
              <YAxis fontSize={10} tickLine={false} stroke="#8A8A93" />
              <Tooltip contentStyle={{ background: "#1A1A1A", border: "none", fontFamily: "JetBrains Mono", fontSize: 11, color: "white" }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Area type="monotone" dataKey="sm07" name="0–10 cm" stroke="#4DA3FF" fill="#4DA3FF" fillOpacity={0.22} strokeWidth={2} />
              <Line type="monotone" dataKey="sm728" name="10–40 cm" stroke="#00FA9A" strokeWidth={2} dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        <div className="eid-card p-4" data-testid="chart-gdd">
          <h3 className="text-lg font-medium">Wärmesumme (GDD, Basis 10 °C)</h3>
          <div className="eid-label mt-1">Kumulativ °C·d</div>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={rows} margin={{ top: 16, right: 12, left: -16, bottom: 0 }}>
              <CartesianGrid stroke="rgba(255,255,255,0.08)" strokeDasharray="2 4" />
              <XAxis dataKey="date" fontSize={10} tickLine={false} stroke="#8A8A93" />
              <YAxis fontSize={10} tickLine={false} stroke="#8A8A93" />
              <Tooltip contentStyle={{ background: "#1A1A1A", border: "none", fontFamily: "JetBrains Mono", fontSize: 11, color: "white" }} />
              <Line type="monotone" dataKey="gdd" stroke="#FF5500" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="eid-card p-4" data-testid="chart-temp">
          <h3 className="text-lg font-medium">Temperatur · 2 m Mittel</h3>
          <div className="eid-label mt-1">°C</div>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={rows} margin={{ top: 16, right: 12, left: -16, bottom: 0 }}>
              <CartesianGrid stroke="rgba(255,255,255,0.08)" strokeDasharray="2 4" />
              <XAxis dataKey="date" fontSize={10} tickLine={false} stroke="#8A8A93" />
              <YAxis fontSize={10} tickLine={false} stroke="#8A8A93" />
              <Tooltip contentStyle={{ background: "#1A1A1A", border: "none", fontFamily: "JetBrains Mono", fontSize: 11, color: "white" }} />
              <Line type="monotone" dataKey="temp" stroke="#FFFFFF" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </section>
    </div>
  );
}
